<template>
  <div>
    <b-alert :variant="alertVariant"
             :show="alertVisible"
             dismissible
             @dismissed="alertVisible=false">
      <span v-html="$t(...alertMessage)"/>
    </b-alert>

    <div class="card">
      <div class="card-header">
        <b>{{ $t('decisions.title') }}</b><br>
        <small>{{ $t('decisions.description') }}</small>
        <button v-if="canUpdateDecision" :class="{ 'btn-loading': busy }" class="btn btn-primary float-right" @click="save()">{{ $t('update') }}</button>
      </div>
      <ul class="list-group list-group-flush">

        <li v-for="(reason, index) in decisions" :class="[ decisions[index].checked ? null : 'text-muted' ]" class="list-group-item">
          <Checkbox :name="`reason${index}`" v-model="decisions[index].checked"><span v-html="$t('decisions.decisions')[index]"/></Checkbox>
          <textarea v-if="decisions[index].checked" :placeholder="createTemplate()" v-model="decisions[index].comment" :readonly="!canUpdateDecision" class="form-control" rows="5" maxlength="3000"/>
        </li>

        <li :class="[ other_decisions.checked ? null : 'text-muted' ]" class="list-group-item">
          <Checkbox v-model="other_decisions.checked" name="other_decisions">{{ $t('decisions.other_decisions') }}</Checkbox>
          <textarea v-if="other_decisions.checked" :placeholder="createTemplate()" v-model="other_decisions.comment" :readonly="!canUpdateDecision" class="form-control" rows="5" maxlength="3000"/>
        </li>

      </ul>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { isManager, isRecipient } from '~/plugins/auth'

export default {
  name: 'Decisions',

  props: {
    recipient: { required: true, type: Object }
  },

  data: () => ({
    alertMessage: [''],
    alertVariant: 'success',
    alertVisible: false,
    busy: false, // Show spinner in button or not.
    decisions: [],
    other_decisions: null,
    template: ''
  }),

  computed: {
    allDecisions () {
      return [ ...this.decisions, this.other_decisions ]
    },
    canUpdateDecision () {
      return !isRecipient(this.user) && !isManager(this.user)
    },
    ...mapGetters({
      nationalities: 'nationalities/nationalities',
      user: 'auth/user'
    })
  },

  watch: {
    canUpdateDecision: function (newV) {
      this.refresh(!newV)
    }
  },

  created () {
    this.template = this.createTemplate()
    this.initializeDecisions()
  },

  mounted () {
    this.load()
    this.refresh(!this.canUpdateDecision)
  },

  methods: {
    alertError (reason) {
      this.alertVariant = 'danger'
      this.alertMessage = ['decisions.error', [ reason ]]
      this.alertVisible = true
    },

    alertSuccess () {
      this.alertVariant = 'success'
      this.alertMessage = ['decisions.success']
      this.alertVisible = true
    },

    createDecision (comment) {
      return { checked: false, comment: comment }
    },

    /**
     * Create template date according to user's locale and country.
     */
    createTemplate () {
      const now = new Date().toUserLocaleString(this)

      return this.$t('decisions.template').join('\n').format([ now ])
    },

    /**
     * Initialize decisions.
     *
     * NOTE : 19 decisions (including one custom).
     */
    initializeDecisions () {
      for (let i = 0; i < 18; i++) {
        this.decisions.push(this.createDecision(this.template))
      }

      this.other_decisions = this.createDecision('')
    },

    /**
     * Load decisions from database.
     *
     * NOTE : As checkboxes values are stored in a suite of bits (11001001...),
     *        we have to check for each checkbox[index] if
     *        suite[length - 1 - index] == 1.
     *
     *        For that, we verify the condition by doing
     *        suite & 2^(index) == 2^(index) which returns true or false.
     */
    async load () {
      const decisions = this.allDecisions

      await axios.get(`/api/users/${this.recipient.id}/evaluations/decisions`)
        .then(function (response) {
          const data = response.data

          decisions.forEach((decision, index) => {
            let flag = Math.pow(2, index)

            // Is checkbox[index] checked ?
            decision.checked = (data.checks & flag) == flag

            // If checkbox is checked, also add its comment.
            if (decision.checked) {
              decision.comment = data.comments[index]
            }
          })
        })
        .catch(function (error) {
          console.error(error)
        })
    },

    /**
     * Save decisions to database.
     *
     * NOTE : Checkboxes values are saved as an array of bits which is converted
     *        as a medium integer. So if you check the first and the third,
     *        you'll get 2⁰ + 2² which is 1 + 4 = 5.
     *
     *        With this way, if you need to do comparisons directly in the
     *        database, like finding which decisions sheets have their third
     *        decision checked, you can do this :
     *        SELECT * FROM evaluations_decisions WHERE checks & 4 = 4
     *
     * NOTE : Comments are stored in an array that is JSONified.
     */
    async save () {
      this.busy = true

      let decisions = this.allDecisions // [ ...this.decisions, this.other_decisions ];

      let checks = 0
      let comments = []

      decisions.forEach((decision, index) => {
        // Compute MediumInt from
        if (decision.checked) {
          checks += Math.pow(2, index)
        }

        // Regroup comments.
        comments.push(decision.checked ? decision.comment : null)
      })

      await axios.patch(`/api/users/${this.recipient.id}/evaluations/decisions`, {
        recipient_id: this.recipient.id,
        checks: checks,
        comments: comments
      })
        .then((response) => {
          this.alertSuccess()
        })
        .catch((error) => {
          console.error(error)
          this.alertError(error.message)
        })

      this.busy = false
    },

    refresh (state) {
      $('.list-group-item').find('input:checkbox').attr('disabled', state)
    }
  }
}
</script>

<style scoped>
.li {
  text-align: justify;
}

textarea {
  margin-top: 1rem;
}

button {
  margin-top: -1rem;
}
</style>



// WEBPACK FOOTER //
// ../../../../resources/js/components/Decisions.vue